// ============================================================================
// JARVISTRAVEL - ROUTE ERROR FALLBACK
// A contained, on-brand recover card shown when a single route crashes (vs. the
// full-screen app-level boundary). Offers an in-place retry and an escape home.
// ============================================================================

import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';

interface RouteErrorFallbackProps {
  /** Re-attempt rendering the crashed route in place. */
  onRetry: () => void;
  /** Leave the broken route (navigate home + clear the error). */
  onHome: () => void;
}

export function RouteErrorFallback({ onRetry, onHome }: RouteErrorFallbackProps) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6 py-12">
      <div className="max-w-sm w-full text-center">
        <div className="mx-auto w-14 h-14 bg-danger/10 text-danger rounded-2xl flex items-center justify-center mb-4">
          <AlertTriangle className="w-7 h-7" strokeWidth={1.75} aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-ink mb-1.5">This screen ran into a problem</h2>
        <p className="text-sm text-muted mb-6">
          Something on this page didn’t load correctly. You can try again, or head back home.
        </p>
        <div className="flex flex-col gap-2">
          <Button variant="primary" fullWidth onClick={onRetry}>Try again</Button>
          <Button variant="ghost" fullWidth onClick={onHome}>Back to home</Button>
        </div>
      </div>
    </div>
  );
}

export default RouteErrorFallback;
